import React from "react";
import { useMap } from "../../contexts/MapContext";
import { useOrg } from "../../contexts/OrganizationContext";
import { useSearch } from "../../contexts/SearchContext";
import { coordInLocale, getPageResultCount } from "../../utils/geo";

const MARKER_IMAGE = "/images/marker.png";
const INDICATE_MARKER_IMAGE = "/images/marker-indicator.png";
const MARKER_SIZE_WIDTH = 56;
const MARKER_SIZE_HEIGHT = 84;
const MARKER_RESIZE_WIDTH = 28;
const MARKER_RESIZE_HEIGHT = 42;
const MARKER_TIP_INSET = 2;
const MARKER_ANCHOR_X = MARKER_RESIZE_WIDTH / 2;
const MARKER_ANCHOR_Y = MARKER_RESIZE_HEIGHT - MARKER_TIP_INSET;
const MARKER_ZINDEX_DEFAULT = typeof google !== 'undefined' ? google.maps.Marker.MAX_ZINDEX : 1000000;
const MARKER_ZINDEX_HIGHLIGHTED = MARKER_ZINDEX_DEFAULT + 1;
const MARKER_ZINDEX_SELECTED = MARKER_ZINDEX_DEFAULT + 2;

function getMarkerIcon(imageUrl) {
  return {
    url: imageUrl,
    size: new google.maps.Size(MARKER_SIZE_WIDTH, MARKER_SIZE_HEIGHT),
    scaledSize: new google.maps.Size(MARKER_RESIZE_WIDTH, MARKER_RESIZE_HEIGHT),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(MARKER_ANCHOR_X, MARKER_ANCHOR_Y),
  };
}

/**
 * MapContent manages markers and info windows imperatively for the persistent map instance.
 * This component doesn't render any DOM - it only manages Google Maps objects via side effects.
 *
 * @param {Array} results - Array of search results to display as markers
 * @param {number|null} selectedResultId - ID of result with open info window
 * @param {number|null} highlightedResultId - ID of highlighted result (different icon)
 * @param {Function} onMarkerClick - Callback when marker is clicked
 * @param {boolean} fitToResults - Whether to fit map bounds to all results
 * @param {Object|null} center - Explicit center {lat, lng} (overrides fitToResults)
 * @param {number|null} zoom - Explicit zoom level (used with center)
 */
function MapContent({
  results = [],
  selectedResultId = null,
  highlightedResultId = null,
  onMarkerClick = null,
  fitToResults = true,
  center = null,
  zoom = null,
}) {
  const { mapInstance, isReady, mapZoom, currentPage: mapPage, fittedResultsKey, setFittedResultsKey } = useMap();
  const { organization } = useOrg();
  const { totalResults, lastPage, params, loadPage } = useSearch();
  const markersRef = React.useRef([]);
  const paginationControlRef = React.useRef(null);
  const markersMapRef = React.useRef(new Map()); // Map of program_site_id -> marker
  const previousSelectedMarkerRef = React.useRef(null); // Track previous selected marker for z-index reset
  const previousHighlightedMarkerRef = React.useRef(null); // Track previous highlighted marker for icon/z-index reset
  const resultsKey = React.useMemo(
    () => results.map((result) => result.program_site_id).join(","),
    [results]
  );

  // Create/update markers when results change
  React.useEffect(() => {
    if (!mapInstance || !isReady) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];
    markersMapRef.current.clear();

    // Create new markers for each result (always use default icon - icons are swapped dynamically)
    results.forEach((result) => {
      if (!result.latLng || !result.latLng.lat || !result.latLng.lng) return;

      const icon = getMarkerIcon(MARKER_IMAGE); // Always start with default icon

      const isTestOrDev =
        typeof window !== "undefined" &&
        (window.RAILS_ENV === "TEST" || window.RAILS_ENV === "development");
      const title = (result.addressLine || "") + (isTestOrDev ? " - GOOGLE MAPS MARKER" : "");

      const marker = new google.maps.Marker({
        position: result.latLng,
        map: mapInstance,
        icon,
        title,
        optimized: !isTestOrDev,
        zIndex: MARKER_ZINDEX_DEFAULT,
      });

      if (onMarkerClick) {
        marker.addListener("click", () => {
          onMarkerClick(result);
        });
      }

      markersRef.current.push(marker);
      markersMapRef.current.set(result.program_site_id, marker);
    });

    return () => {
      // Cleanup markers on unmount
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
      markersMapRef.current.clear();
    };
  }, [mapInstance, isReady, results, onMarkerClick]);

  // Manage selected marker z-index. The selected result card is rendered outside the map
  // so it can stay fixed to the bottom of the map view.
  React.useEffect(() => {
    if (!mapInstance || !isReady) return;

    if (selectedResultId) {
      const marker = markersMapRef.current.get(selectedResultId);

      if (marker) {
        // Reset previous selected marker z-index (if switching to a different marker)
        if (previousSelectedMarkerRef.current !== null && 
            previousSelectedMarkerRef.current !== selectedResultId) {
          const prevMarker = markersMapRef.current.get(previousSelectedMarkerRef.current);
          if (prevMarker) {
            prevMarker.setZIndex(MARKER_ZINDEX_DEFAULT);
          }
        }

        // Bring selected marker to front (highest z-index)
        marker.setZIndex(MARKER_ZINDEX_SELECTED);
        previousSelectedMarkerRef.current = selectedResultId;
      }
    } else {
      // Reset selected marker z-index when closing info window
      if (previousSelectedMarkerRef.current !== null) {
        const prevMarker = markersMapRef.current.get(previousSelectedMarkerRef.current);
        if (prevMarker) {
          prevMarker.setZIndex(MARKER_ZINDEX_DEFAULT);
        }
        previousSelectedMarkerRef.current = null;
      }

    }
  }, [mapInstance, isReady, selectedResultId]);

  // Manage highlighted marker icon and z-index (dynamic icon swapping without marker recreation)
  React.useEffect(() => {
    if (!mapInstance || !isReady) return;

    // Reset previous highlighted marker (restore default icon and z-index)
    if (previousHighlightedMarkerRef.current !== null) {
      const prevMarker = markersMapRef.current.get(previousHighlightedMarkerRef.current);
      // Only reset if it's not currently selected (selected markers maintain highest z-index)
      if (prevMarker && previousHighlightedMarkerRef.current !== selectedResultId) {
        prevMarker.setIcon(getMarkerIcon(MARKER_IMAGE));
        prevMarker.setZIndex(MARKER_ZINDEX_DEFAULT);
      }
    }

    // Set new highlighted marker (highlight icon and elevated z-index)
    if (highlightedResultId && highlightedResultId !== selectedResultId) {
      const marker = markersMapRef.current.get(highlightedResultId);
      if (marker) {
        marker.setIcon(getMarkerIcon(INDICATE_MARKER_IMAGE));
        marker.setZIndex(MARKER_ZINDEX_HIGHLIGHTED);
      }
      previousHighlightedMarkerRef.current = highlightedResultId;
    } else if (highlightedResultId === null) {
      previousHighlightedMarkerRef.current = null;
    }
  }, [mapInstance, isReady, highlightedResultId, selectedResultId]);

  // Handle map positioning (fit bounds or explicit center/zoom)
  React.useEffect(() => {
    if (!mapInstance || !isReady) return;

    if (center && zoom !== null && zoom !== undefined) {
      // Explicit center/zoom (typically for detail page)
      // Use panTo for smooth animation instead of setCenter (prevents tile loading jumps)
      // Matches Marionette selectMarker behavior (map.coffee:172-174)
      const currentZoom = mapInstance.getZoom();
      
      // Only zoom in if currently zoomed out (preserve user's zoom if already closer)
      if (currentZoom < zoom) {
        mapInstance.setZoom(zoom);
      }
      
      // panTo() provides smooth animation regardless of distance
      mapInstance.panTo(center);
    } else if (fitToResults && results.length > 0 && fittedResultsKey !== resultsKey) {
      // Fit bounds to all markers (typically for search page)
      const bounds = new google.maps.LatLngBounds();
      let validMarkers = 0;
      const hasLocale = organization.serviceLocale?.geo?.bounds;

      results.forEach((result) => {
        if (result.latLng && result.latLng.lat && result.latLng.lng) {
          if (!hasLocale || coordInLocale(result.latLng, organization)) {
            bounds.extend(result.latLng);
            validMarkers++;
          }
        }
      });

      if (validMarkers > 0) {
        setFittedResultsKey(resultsKey);
        // If mapZoom is set (user dragged map), use that zoom level instead of fitBounds
        // This matches Marionette behavior (map.coffee:164-165)
        if (mapZoom !== null && mapZoom !== undefined) {
          mapInstance.setZoom(mapZoom);
        } else {
          mapInstance.fitBounds(bounds);
          if (validMarkers === 1) {
            // For single marker, zoom in after fitBounds completes
            google.maps.event.addListenerOnce(mapInstance, "idle", () => {
              mapInstance.setZoom(15);
            });
          }
        }
      }
    }
  }, [mapInstance, isReady, results, fitToResults, center, zoom, organization, mapZoom, fittedResultsKey, setFittedResultsKey, resultsKey]);

  // Snapshot the location that corresponds to the current results — only update when results
  // change so the displayed location stays in sync with what was actually searched, not the
  // live input value which can change before a new search runs.
  const [displayLocation, setDisplayLocation] = React.useState(params.location || "");
  React.useEffect(() => {
    setDisplayLocation(params.location || "");
  }, [results]); // results prop changes on each completed search

  // Manage the map pagination control (TOP_CENTER custom Google Maps control)
  React.useEffect(() => {
    if (!mapInstance || !isReady) return;

    const controls = mapInstance.controls[google.maps.ControlPosition.TOP_CENTER];

    if (mapPage !== "search" || !totalResults) {
      controls.clear();
      paginationControlRef.current = null;
      return;
    }

    const currentPageNum = params.page || 1;
    const perPage = params.per_page || 25;
    const resultCount = getPageResultCount(totalResults, perPage, currentPageNum, lastPage);
    const location = displayLocation;
    const isFirstPage = currentPageNum <= 1;
    const isLastPage = currentPageNum >= lastPage;
    const multiPage = totalResults > perPage;

    if (!paginationControlRef.current) {
      const el = document.createElement("div");
      paginationControlRef.current = el;
      controls.clear();
      controls.push(el);
    }

    const el = paginationControlRef.current;
    el.className = "map-pagination-control";
    el.innerHTML = `
      <div class="control-content">
        ${multiPage ? `<button class="back-arrow${isFirstPage ? " back-disabled disabled" : ""}" aria-label="Previous" type="button"></button>` : ""}
        <div class="text-container">
          <p class="control-text">${resultCount}</p>
          ${location ? `<p>for ${location}</p>` : ""}
        </div>
        ${multiPage ? `<button class="forward-arrow${isLastPage ? " forward-disabled disabled" : ""}" aria-label="Next" type="button"></button>` : ""}
      </div>
    `;

    // Buttons are recreated via innerHTML each run, so no listener cleanup needed
    const backBtn = el.querySelector(".back-arrow:not(.disabled)");
    const fwdBtn = el.querySelector(".forward-arrow:not(.disabled)");
    if (backBtn) backBtn.addEventListener("click", () => loadPage("previous"));
    if (fwdBtn) fwdBtn.addEventListener("click", () => loadPage("next"));
  }, [mapInstance, isReady, mapPage, totalResults, lastPage, params.page, params.per_page, displayLocation, loadPage]);

  // This component only manages side effects, no DOM rendering
  return null;
}

export default MapContent;
